/**
 * Read-only capability probes for the diagnostics page and, from Phase 8, the workshop's
 * WebGL2 gate (WORLD-RENDER-001). WebGL2 is the baseline; WebGPU is informational only.
 */
export interface Capabilities {
  webgl2: boolean;
  webgpu: boolean;
  indexedDb: boolean;
  storageEstimate: { usageBytes: number; quotaBytes: number } | null;
  persistedStorage: boolean | null;
  reducedMotion: boolean;
  userAgent: string;
}

export function detectWebGl2(doc: Document = document): boolean {
  try {
    const canvas = doc.createElement('canvas');
    const ctx = canvas.getContext('webgl2');
    const ok = ctx !== null && ctx !== undefined;
    (ctx as WebGL2RenderingContext | null)?.getExtension('WEBGL_lose_context')?.loseContext();
    return ok;
  } catch {
    // A throwing getContext means the capability is absent; the result itself is the diagnostic.
    return false;
  }
}

export async function detectCapabilities(): Promise<Capabilities> {
  let storageEstimate: Capabilities['storageEstimate'] = null;
  let persistedStorage: boolean | null = null;
  if (navigator.storage?.estimate) {
    const est = await navigator.storage.estimate();
    storageEstimate = { usageBytes: est.usage ?? 0, quotaBytes: est.quota ?? 0 };
  }
  if (navigator.storage?.persisted) persistedStorage = await navigator.storage.persisted();
  return {
    webgl2: detectWebGl2(),
    webgpu: 'gpu' in navigator,
    indexedDb: typeof indexedDB !== 'undefined',
    storageEstimate,
    persistedStorage,
    reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    userAgent: navigator.userAgent,
  };
}
